/* ==========================================================================
   Internal Login Page
   Task F33: Super-admin login for internal tools
   ========================================================================== */

import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { FormField } from '@/components/ui/FormField'
import { Alert } from '@/components/ui/Alert'
import { TwoFactorCodeStep } from '@/components/auth/TwoFactorCodeStep'
import {
  internalLogin,
  internalLoginTwoFactor,
  getInternalToken,
  isInternalTokenExpired,
} from '@/api/internal'
import { ApiClientError } from '@/api/client'
import { isTwoFactorChallenge } from '@/api/types'

export function InternalLogin() {
  const navigate = useNavigate()

  // If already authenticated, redirect to checkout
  useEffect(() => {
    const token = getInternalToken()
    if (token && !isInternalTokenExpired()) {
      navigate('/internal/checkout', { replace: true })
    }
  }, [navigate])

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()
  // Set when the password was right but a 2FA code is needed (PORTAL-187).
  const [twoFactorToken, setTwoFactorToken] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email.trim() || !password) {
      setError('Email and password are required')
      return
    }

    setLoading(true)
    setError(undefined)

    try {
      const result = await internalLogin(email.trim().toLowerCase(), password)
      if (isTwoFactorChallenge(result)) {
        setTwoFactorToken(result.two_factor_token)
        return
      }
      navigate('/internal/checkout', { replace: true })
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.getUserMessage())
      } else {
        setError('An unexpected error occurred. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleTwoFactorSubmit = async (code: string, rememberDevice: boolean) => {
    if (!twoFactorToken) return
    setLoading(true)
    setError(undefined)

    try {
      await internalLoginTwoFactor(
        { two_factor_token: twoFactorToken, code, remember_device: rememberDevice },
        email.trim().toLowerCase()
      )
      navigate('/internal/checkout', { replace: true })
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.getUserMessage()
          : 'An unexpected error occurred. Please try again.'
      if (message.includes('expired')) {
        setTwoFactorToken(null)
      }
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  if (twoFactorToken) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-warm-beige p-4">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <span className="font-serif text-2xl text-deep-purple">TJM Labs</span>
          </div>
          <Card className="p-6 sm:p-8">
            <TwoFactorCodeStep
              onSubmit={handleTwoFactorSubmit}
              onCancel={() => {
                setTwoFactorToken(null)
                setError(undefined)
              }}
              loading={loading}
              error={error}
            />
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-warm-beige p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <Link to="/" className="cursor-pointer">
            <span className="font-serif text-2xl text-deep-purple">
              TJM Labs
            </span>
          </Link>
        </div>

        <Card className="p-6 sm:p-8">
          <div className="text-center mb-6">
            <h1 className="font-serif text-2xl sm:text-3xl text-charcoal">
              Internal Tools
            </h1>
            <p className="text-charcoal/70 mt-2">
              Sign in with your super-admin account
            </p>
          </div>

          {error && (
            <Alert variant="error" className="mb-6">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="space-y-5">
              <FormField label="Email" required htmlFor="internal-email">
                <Input
                  id="internal-email"
                  type="email"
                  autoComplete="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(undefined) }}
                  disabled={loading}
                />
              </FormField>

              <FormField label="Password" required htmlFor="internal-password">
                <div className="relative">
                  <Input
                    id="internal-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(undefined) }}
                    disabled={loading}
                    className="pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-charcoal transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender rounded"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    disabled={loading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <Eye className="h-5 w-5" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </FormField>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={loading}
              disabled={loading}
              className="w-full mt-6"
            >
              Sign In
            </Button>
          </form>
        </Card>

        <div className="mt-6 text-center">
          <Link
            to="/"
            className="text-sm text-charcoal/60 hover:text-charcoal transition-colors"
          >
            Back to home
          </Link>
        </div>
      </div>
    </div>
  )
}

export default InternalLogin
